import { AnimatePresence, motion, useInView } from 'framer-motion';
import {
  ArrowRight, Brain, Briefcase, CheckCircle2,
  ChevronDown, Code2, Eye, IndianRupee, Mail, Phone,
  Rocket, ShieldCheck, Sparkles, Star, Target, TrendingUp, User, Users, X,
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { GatedVideo, ViewerProvider } from '../components/GatedVideo';
import logo from '../logo/lo.png';

// Lead goes straight to the CRM server — creates an Unassigned Lead and sends
// the Brevo confirmation email (no Google Apps Script involved).
const API_BASE = 'https://vistatech-serversss-production.up.railway.app';
const WHATSAPP_LINK = 'https://wa.me/919894449002';
const WHATSAPP_TRIAL_LINK = 'https://wa.me/919894449002?text=' + encodeURIComponent("Hi Vista Tech, I'd like to register for the ₹1000 / 1-week live trial session.");
const PHONE_DISPLAY = '9894449002';

function useSEO() {
  useEffect(() => {
    const prevTitle = document.title;
    document.title = 'Enrollment — Pay After Placement Gen AI Programs | Vista Tech';
    return () => { document.title = prevTitle; };
  }, []);
}

// ─── Animated counter (matches Home.tsx pattern) ───────────────────────────
const Counter = ({ target, suffix = '' }: { target: number; suffix?: string }) => {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const inView = useInView(ref, { once: true });

  useEffect(() => {
    if (!inView) return;
    let start = 0;
    const duration = 1400;
    const step = Math.max(1, Math.ceil(target / (duration / 16)));
    const timer = setInterval(() => {
      start += step;
      if (start >= target) { setCount(target); clearInterval(timer); }
      else setCount(start);
    }, 16);
    return () => clearInterval(timer);
  }, [inView, target]);

  return <span ref={ref}>{count.toLocaleString()}{suffix}</span>;
};

// ─── Data ───────────────────────────────────────────────────────────────────
const TRACKS = [
  {
    id: 'aiml',
    tab: 'Gen AI · AIML',
    duration: '45-Day Program',
    title: 'Gen AI - AIML',
    Icon: Brain,
    color: '#4e3a69',
    desc: 'From Python foundations to classical ML, deep learning, transformers, LLMs, RAG and MLOps deployment — a full path into applied AI engineering.',
    domains: ['Python', 'SQL', 'Machine Learning', 'Deep Learning', 'LLMs & RAG', 'MLOps'],
    roles: ['Machine Learning Engineer', 'AI Engineer', 'Generative AI Engineer', 'NLP Engineer', 'Computer Vision Engineer', 'MLOps Engineer', 'Applied ML Engineer', 'AI/ML Intern / Trainee'],
  },
  {
    id: 'fsa',
    tab: 'Full Stack Analytics',
    duration: '60-Day Program',
    title: 'Gen AI - Full Stack Analytics',
    Icon: Code2,
    color: '#FF6535',
    desc: 'Business analyst foundations, Excel, SQL, Python, Power BI and Tableau, plus Jira, Trello, Miro and Confluence — a complete analytics + BA skill stack.',
    domains: ['Excel', 'SQL', 'Python', 'Power BI', 'Tableau', 'Jira / Agile'],
    roles: ['Business Analyst', 'Data Analyst', 'Power BI Developer', 'Tableau Developer / Analyst', 'MIS Executive / Analyst', 'Business Intelligence Analyst', 'Operations Analyst', 'Agile Business Analyst'],
  },
];

const TIMELINE = [
  { day: 'DAY 01', title: 'Training start', desc: 'Live, mentor-led cohort sessions begin — online or in person, your choice.' },
  { day: 'DAY 30', title: 'Internal interview for internship', desc: 'An internal panel evaluates your progress and readiness so far.' },
  { day: 'DAY 60', title: 'Training completion', desc: 'Curriculum, capstone project and portfolio are complete.' },
  { day: 'DAY 61', title: 'Official interview', desc: 'Profile validation and a guided mock-to-real interview process begins.' },
  { day: 'DAY 71', title: 'HR consultancy placement drive', desc: 'Infinite HR Consultants maps your validated profile against live vacancies.' },
  { day: 'DAY 90', title: 'Offer letter', desc: "You're placed. This is when the second half of your fee becomes due." },
  { day: 'DAY 100', title: 'Remaining 50% fee settled', desc: 'Not before. Outcomes first, payment second.' },
];

const WHY = [
  { Icon: Users, title: 'Live interactive sessions', desc: 'Real-time classes with mentors, not pre-recorded playlists.' },
  { Icon: Target, title: 'Hands-on project experience', desc: 'Capstone projects built for your portfolio, not just your certificate.' },
  { Icon: Briefcase, title: 'HR consultancy placement support', desc: 'A dedicated recruitment partner, not a generic job board.' },
  { Icon: ShieldCheck, title: 'No third-party job apps required', desc: 'Placement runs through Vista Tech and Infinite HR — nowhere else.' },
];

const MENTORS = [
  { initials: 'NK', name: 'Naveen Krishna', role: 'Co-Founder · Director · Career Strategist', quote: '"I believe even the most complex tech can be made simple — if taught with the right example. That\'s what we do at Vista Tech."', bg: '#2b2240' },
  { initials: 'DP', name: 'Dhanush Priya', role: 'Co-Founder · Chief Mentor · Full Stack', quote: '"My goal is to make every student feel confident — whether it\'s in a code review or an interview room."', bg: '#4e3a69' },
  { initials: 'HU', name: 'Harish UP', role: 'Co-Founder · Chief Mentor · Digital Marketing', quote: '"I\'ve seen students go from zero to getting clients and job offers in 90 days. All they need is the right structure and mentorship."', bg: '#FF6535' },
];

const FAQS = [
  { q: "What happens if I don't get placed?", a: 'The remaining 50% of your fee is only payable after successful placement (around Day 90–100). If placement doesn\'t happen, that second installment is never charged.' },
  { q: 'Do I need prior coding experience?', a: 'No. Every track starts from the fundamentals — Python basics, data types and control flow — before moving into the specialised curriculum, so complete beginners and non-IT career switchers can join.' },
  { q: 'How much does the program cost?', a: 'Online group sessions are ₹30,000 in total: ₹15,000 to begin training and ₹15,000 only once you have an offer letter. Offline (in-person) group sessions are ₹40,000: ₹20,000 to begin and ₹20,000 after placement. Both follow the identical Day 1–100 placement structure.' },
  { q: 'How does the HR consultancy placement drive work?', a: 'After training completion and internal profile validation (around Day 61–71), your validated profile is mapped against live vacancies by Infinite HR Consultants — our placement partner with 20+ years of recruitment experience.' },
  { q: 'Who can join these programs?', a: 'Freshers wanting real-time IT skills, college students seeking placements or internships, career switchers moving from non-IT backgrounds, and anyone ready to commit to a structured 45–60 day program.' },
  { q: 'What is the ₹1000 trial week?', a: 'A low-commitment way to experience Vista Tech before enrolling in a full track — register for ₹1000 and attend one full week of live sessions with a mentor.' },
  { q: 'Where is Vista Tech based?', a: `Our corporate office is at No.115, 7th Floor, Metro Towers, PH Road, Egmore, Chennai – 600 084. Call ${PHONE_DISPLAY} to speak with the team directly.` },
];

// Real, named outcomes as published in Vista Tech's HR Consultancy brochure.
const SUCCESS_STORIES = [
  { name: 'Rajesh P.', edu: 'BCA', role: 'Operations Analyst', company: 'Contus', salary: '3.5 LPA' },
  { name: 'Karthik S.', edu: 'B.Sc CSE', role: 'Operations Analyst', company: 'CBTS', salary: '3.8 LPA' },
  { name: 'Arjun', edu: 'B.Tech IT', role: 'Junior Data Scientist', company: 'Changbee', salary: '3.6 LPA' },
  { name: 'Vignesh R.', edu: 'B.Tech AI/DS', role: 'Business Analyst', company: 'Prodapt', salary: '4.5 LPA' },
  { name: 'Akash V.', edu: 'M.E CSE', role: 'AI/ML Developer', company: 'Digil Technologies', salary: '5.0 LPA' },
  { name: 'Ajay Krishna', edu: 'BCA', role: 'Power BI Developer', company: 'Hansiba Technologies', salary: '3.8 LPA' },
  { name: 'Rohit Prakash', edu: 'MCA', role: 'Data Analyst', company: 'Kyndryl', salary: '4.0 LPA' },
  { name: 'Vikram N.', edu: 'BE CSE', role: 'Power BI Developer', company: 'Zillion Technologies', salary: '3.5 LPA' },
  { name: 'Meghana K.', edu: 'B.Tech IT', role: 'Data Scientist', company: 'Cognizant', salary: '5.0 LPA' },
  { name: 'Priyadharshini M.', edu: 'BCA', role: 'Data Analyst', company: 'Cerebra', salary: '4.2 LPA' },
  { name: 'Naveen Raj', edu: 'BE CSE', role: 'AI/ML Developer', company: 'Fujitsu', salary: '4.5 LPA' },
  { name: 'Divya R.', edu: 'BCA', role: 'Business Analyst', company: 'Sensiple', salary: '3.5 LPA' },
  { name: 'Saravanan M.', edu: 'B.Tech IT', role: 'Data Analyst', company: 'Kyndryl', salary: '4.0 LPA' },
  { name: 'Manikandan S.', edu: '', role: 'AI/ML Developer', company: 'Fujitsu', salary: '4.5 LPA' },
  { name: 'Dinesh Kumar', edu: 'B.Sc CSE', role: 'Business Analyst', company: 'Prodapt', salary: '3.7 LPA' },
];

// Curated, real Google Reviews as published in Vista Tech's HR Consultancy brochure.
const REVIEWS = [
  { name: 'Rajeshwari S', rating: 5, time: '4 months ago', text: "Vista Tech transformed my skills! I joined without any coding background, and now I'm developing full-stack apps confidently. Projects are real-world oriented — not just theory. Totally worth it!" },
  { name: 'Chanthini Selvam', rating: 5, time: '3 weeks ago', text: 'Being a fresher, 1:1 training was very useful to me — got selected as Data Analytics Intern with a stipend of ₹22K. Thank you Priya mam and Harish sir!' },
  { name: 'D21CA064 ramya.S', rating: 5, time: '3 weeks ago', text: 'Completed training with internship and got placed as Junior Business Analyst at 3.4 LPA. Thank you Naveen sir for your guidance and mentorship.' },
  { name: 'Pooja Sivakumar', rating: 5, time: '9 months ago', text: 'Completed my Java Full Stack Development course here — an amazing experience. Thanks to their guidance and placement support, I got placed in a good company right after finishing.' },
  { name: 'Deepa Murali', rating: 5, time: '3 months ago', text: 'The placement team provided continuous support — resume building, mock interviews, and interview scheduling. Because of their guidance, I secured a job in a reputed company.' },
  { name: "Dr.Arul mathi", rating: 5, time: '3 weeks ago', text: "We reached out to Vista Tech for our sister's training. With your guidance she got placed as a Junior Data Analyst within 2 months. Thank you for all your help!" },
  { name: 'Ganesh Kota', rating: 4, time: '3 months ago', text: 'The trainers are very experienced and explain even complex topics simply. Practical training, real-time projects and excellent placement assistance throughout.' },
  { name: 'Honey language', rating: 5, time: '3 weeks ago', text: 'Good training and support — selected as Data Analyst Intern within 40 days.' },
  { name: 'Logesh S', rating: 5, time: '3 months ago', text: 'Excellent institute for both learning and placement. Trainers ensure you understand concepts deeply, and the placement support is outstanding.' },
];

const HIRING_PARTNERS = [
  'CBTS', 'Kyndryl', 'TAO Digital', 'Sensiple', 'Prodapt', 'CAPCO', 'Zillion Technologies',
  'Fujitsu', 'Contus Tech', 'Digile', 'Orion Systems', 'ManVision', 'Cerebra', 'iOPEX',
  'Wipro', 'ZoomInfo', 'Cognizant', 'Changbee', 'Hansiba Technologies', 'Digil Technologies',
];

const NORMAL_STEPS = ['Skill', 'Internship', 'Portfolio', 'Certificate', 'Resume', 'LinkedIn / Naukri'];
const VISTA_STEPS = [
  { title: 'Skill Building', desc: 'Live, role-based technical training.' },
  { title: 'Internal Assessment', desc: 'Vista Tech evaluates your readiness first.' },
  { title: 'HR Consultancy Placement Drive', desc: 'Infinite HR maps you directly to live vacancies.' },
  { title: 'Direct Final Round', desc: 'You skip the resume pile entirely.' },
  { title: 'Offer Letter', desc: 'No 7% odds. No third-party job apps.' },
];

const WHY_HR = [
  { Icon: Eye, title: 'Less Competition', desc: 'You\'re not one of thousands applying cold on a job board.' },
  { Icon: ShieldCheck, title: 'More Transparency', desc: 'Real vacancies, mapped directly by a recruitment partner.' },
  { Icon: TrendingUp, title: 'High Probability of Getting Placed', desc: 'A validated profile beats a resume in a pile of thousands.' },
  { Icon: IndianRupee, title: 'Better Salary Package', desc: 'Consultancy-backed candidates negotiate from a position of proof.' },
];

type FormState = { name: string; phone: string; email: string; track: string };
const EMPTY_FORM: FormState = { name: '', phone: '', email: '', track: '' };

// ─── Star rating ────────────────────────────────────────────────────────────
const Stars = ({ n }: { n: number }) => (
  <div className="flex gap-0.5">
    {Array.from({ length: 5 }).map((_, i) => (
      <Star key={i} size={13} className={i < n ? 'text-accent fill-accent' : 'text-primary/15 fill-primary/15'} />
    ))}
  </div>
);

// ─── Lead form (reused in hero + final CTA) ────────────────────────────────
const LeadForm = ({ idPrefix, compact = false }: { idPrefix: string; compact?: boolean }) => {
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [errors, setErrors] = useState<Partial<Record<keyof FormState, boolean>>>({});
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');

  const handle = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    setForm(p => ({ ...p, [e.target.name]: e.target.value }));

  const validate = () => {
    const next: Partial<Record<keyof FormState, boolean>> = {};
    if (form.name.trim().length < 2) next.name = true;
    if (!/^[0-9]{10}$/.test(form.phone.trim())) next.phone = true;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) next.email = true;
    if (!form.track) next.track = true;
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    setStatus('submitting');
    try {
      const response = await fetch(`${API_BASE}/api/enroll-interest`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: form.name, phone: form.phone, email: form.email,
          course: form.track,
        }),
      });
      const result = await response.json();
      if (result.ok) setStatus('success');
      else setStatus('error');
    } catch {
      setStatus('error');
    }
  };

  if (status === 'success') {
    return (
      <div className="text-center py-6 px-2">
        <div className="w-14 h-14 rounded-full bg-accent flex items-center justify-center mx-auto mb-4">
          <CheckCircle2 className="text-primary" size={28} strokeWidth={2.5} />
        </div>
        <h3 className="text-white text-xl font-bold mb-2">You're on the list.</h3>
        <p className="text-[#c2b6d4] text-sm mb-5 leading-relaxed">
          A Vista Tech mentor will call you within 24 hours. Keep your phone handy — that call could start your Day 1.
        </p>
        <a
          href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer"
          className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-[#25D366] text-white font-semibold text-sm shadow-lg shadow-[#25D366]/30"
        >
          Chat on WhatsApp instead
        </a>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="flex flex-col gap-3.5" noValidate>
      <div className="flex flex-wrap items-center gap-2 mb-0.5">
        <span className="inline-flex items-center gap-1.5 text-[11px] font-black uppercase tracking-wide bg-accent text-primary px-2.5 py-1 rounded-full">
          <IndianRupee size={11} strokeWidth={3} /> 50% Pay After Placement
        </span>
        <span className="uppercase text-[11px] tracking-widest font-bold text-orange">Free · No Obligation</span>
      </div>
      <h3 className={`text-white font-extrabold ${compact ? 'text-lg' : 'text-xl sm:text-2xl'} leading-snug`}>
        Get your free career counselling call
      </h3>
      {!compact && (
        <p className="text-[#a89bbd] text-sm -mt-1 mb-1">
          A mentor calls you within 24 hours to map the right track for your background. Pay only 50% to start —
          the rest is due only after you're placed.
        </p>
      )}
      <p className="flex items-center gap-1.5 text-[11px] text-[#9c8fb3] -mt-1 mb-1">
        <Users size={12} className="text-accent" /> Join 150+ students already placed through Vista Tech
      </p>

      <div>
        <label htmlFor={`${idPrefix}-name`} className="block text-[11px] uppercase tracking-wide text-[#8f81a8] mb-1.5 font-semibold">Full name</label>
        <div className="relative">
          <User size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#756890]" />
          <input
            id={`${idPrefix}-name`} name="name" type="text" value={form.name} onChange={handle}
            placeholder="Your name"
            className={`w-full pl-9 pr-3 py-3 rounded-lg bg-white/5 border text-white text-sm placeholder-white/30 outline-none transition-colors ${errors.name ? 'border-orange' : 'border-white/15 focus:border-accent'}`}
          />
        </div>
        {errors.name && <p className="text-orange text-xs mt-1">Please enter your name.</p>}
      </div>

      <div>
        <label htmlFor={`${idPrefix}-phone`} className="block text-[11px] uppercase tracking-wide text-[#8f81a8] mb-1.5 font-semibold">Phone number</label>
        <div className="relative">
          <Phone size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#756890]" />
          <input
            id={`${idPrefix}-phone`} name="phone" type="tel" value={form.phone} onChange={handle}
            placeholder="10-digit mobile number"
            className={`w-full pl-9 pr-3 py-3 rounded-lg bg-white/5 border text-white text-sm placeholder-white/30 outline-none transition-colors ${errors.phone ? 'border-orange' : 'border-white/15 focus:border-accent'}`}
          />
        </div>
        {errors.phone && <p className="text-orange text-xs mt-1">Enter a valid 10-digit number.</p>}
      </div>

      <div>
        <label htmlFor={`${idPrefix}-email`} className="block text-[11px] uppercase tracking-wide text-[#8f81a8] mb-1.5 font-semibold">Email</label>
        <div className="relative">
          <Mail size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#756890]" />
          <input
            id={`${idPrefix}-email`} name="email" type="email" value={form.email} onChange={handle}
            placeholder="you@example.com"
            className={`w-full pl-9 pr-3 py-3 rounded-lg bg-white/5 border text-white text-sm placeholder-white/30 outline-none transition-colors ${errors.email ? 'border-orange' : 'border-white/15 focus:border-accent'}`}
          />
        </div>
        {errors.email && <p className="text-orange text-xs mt-1">Enter a valid email address.</p>}
      </div>

      <div>
        <label htmlFor={`${idPrefix}-track`} className="block text-[11px] uppercase tracking-wide text-[#8f81a8] mb-1.5 font-semibold">Interested track</label>
        <select
          id={`${idPrefix}-track`} name="track" value={form.track} onChange={handle}
          className={`w-full px-3 py-3 rounded-lg bg-[#1a1530] border text-sm outline-none transition-colors ${form.track ? 'text-white' : 'text-[#8f81a8]'} ${errors.track ? 'border-orange' : 'border-white/15 focus:border-accent'}`}
        >
          <option value="">Choose a track</option>
          <option value="Gen AI - AIML">Gen AI - AIML</option>
          <option value="Gen AI - Full Stack Analytics (Data analytics and Business analyst)">Gen AI - Full Stack Analytics(Data analytics and Business analyst)</option>
          <option value="₹1000 Trial Week">₹1000 Trial Week — not ready to commit yet</option>
          <option value="Not sure yet">Not sure yet — help me choose</option>
        </select>
        {errors.track && <p className="text-orange text-xs mt-1">Please choose a track.</p>}
      </div>

      {status === 'error' && (
        <p className="text-orange text-xs text-center">
          Couldn't submit. <a href={WHATSAPP_LINK} className="underline">WhatsApp us instead</a>
        </p>
      )}

      <motion.button
        whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }} type="submit" disabled={status === 'submitting'}
        className="w-full py-3.5 rounded-lg bg-accent text-primary font-bold text-sm flex items-center justify-center gap-2 shadow-lg shadow-accent/30 disabled:opacity-60"
      >
        {status === 'submitting' ? 'Submitting…' : <>Get My Free Career Call — No Payment Now <ArrowRight size={16} /></>}
      </motion.button>
      <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1.5 pt-1">
        {['50% due only after placement', '24-hr callback', 'No spam, ever'].map(t => (
          <span key={t} className="flex items-center gap-1.5 text-[11px] text-[#9c8fb3]">
            <CheckCircle2 size={12} className="text-accent" /> {t}
          </span>
        ))}
      </div>
    </form>
  );
};

// ─── Intro video section ────────────────────────────────────────────────────
const IntroVideo = () => (
  <section id="intro-video" className="py-14 sm:py-20 bg-dark">
    <div className="max-w-4xl mx-auto px-4 sm:px-6">
      <Reveal className="text-center mb-8">
        <p className="uppercase text-[12px] tracking-widest font-bold text-accent mb-3">Watch first</p>
        <h2 className="text-white text-3xl sm:text-4xl font-black leading-tight mb-3">
          What is Vista Tech, and how does this actually work?
        </h2>
        <p className="text-[#a89bbd] max-w-xl mx-auto text-sm sm:text-base">
          A short walkthrough of the pay-after-placement process, straight from the team — before you scroll any further.
        </p>
      </Reveal>
      <Reveal>
        <GatedVideo src="/video/intro.mp4" sourceLabel="Enrollment Intro Video" playLabel="Watch the Intro Video" className="shadow-2xl" />
      </Reveal>
    </div>
  </section>
);

// ─── Reveal wrapper ─────────────────────────────────────────────────────────
const Reveal = ({ children, className = '' }: { children: React.ReactNode; className?: string }) => (
  <motion.div
    initial={{ opacity: 0, y: 22 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: '-60px' }}
    transition={{ duration: 0.55, ease: 'easeOut' }}
    className={className}
  >
    {children}
  </motion.div>
);

// ─── Page ───────────────────────────────────────────────────────────────────
const Enrollment = () => {
  useSEO();
  const [activeTrack, setActiveTrack] = useState(0);
  const [openFaq, setOpenFaq] = useState<number | null>(0);
  const [showStickyCta, setShowStickyCta] = useState(false);
  const [showTrialBanner, setShowTrialBanner] = useState(true);
  const heroRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = heroRef.current;
    if (!el) return;
    const io = new IntersectionObserver(([entry]) => setShowStickyCta(!entry.isIntersecting), { threshold: 0 });
    io.observe(el);
    return () => io.disconnect();
  }, []);

  const track = TRACKS[activeTrack];

  return (
    <ViewerProvider>
    <div className="font-poppins bg-background text-primary">
      {/* ── Minimal top bar ── */}
      <header className="sticky top-0 z-40 bg-dark/90 backdrop-blur-md border-b border-white/10">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img src={logo} alt="Vista Tech" className="h-8 object-contain" />
          </Link>
          <div className="hidden sm:flex items-center gap-6 text-xs text-[#a89bbd] font-medium">
            <a href="#tracks" className="hover:text-accent transition-colors">Tracks</a>
            <a href="#process" className="hover:text-accent transition-colors">Process</a>
            <a href="#success" className="hover:text-accent transition-colors">Success Stories</a>
            <a href="#pricing" className="hover:text-accent transition-colors">Pricing</a>
            <a href="#faq" className="hover:text-accent transition-colors">FAQ</a>
          </div>
          <a href="#lead-form" className="px-4 py-2 rounded-full bg-accent text-primary text-xs font-bold whitespace-nowrap">
            Free Career Call
          </a>
        </div>
      </header>

      {/* ── ₹1000 trial-week banner ── */}
      {showTrialBanner && (
        <div className="bg-accent-dark text-white">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 py-2.5 flex items-center justify-center gap-3 text-center relative">
            <p className="text-xs sm:text-sm font-semibold">
              Not ready to commit? Start your IT career for <span className="font-black">₹1000</span> — get a full week of live sessions.
            </p>
            <a href={WHATSAPP_TRIAL_LINK} target="_blank" rel="noopener noreferrer"
              className="hidden sm:inline-flex items-center gap-1 text-xs font-bold underline underline-offset-2 whitespace-nowrap">
              Reserve my spot <ArrowRight size={12} />
            </a>
            <button aria-label="Dismiss" onClick={() => setShowTrialBanner(false)} className="absolute right-4 sm:right-6 text-[#dcd2e8] hover:text-white">
              <X size={15} />
            </button>
          </div>
        </div>
      )}

      {/* ── Intro video ── */}
      <IntroVideo />

      {/* ── Hero ── */}
      <section ref={heroRef} className="relative overflow-hidden bg-gradient-to-br from-primary via-primary to-dark">
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          <div className="absolute -top-40 -left-32 w-[420px] h-[420px] rounded-full bg-secondary/30 blur-3xl" />
          <div className="absolute -bottom-32 -right-20 w-[340px] h-[340px] rounded-full bg-accent/10 blur-3xl" />
          <div className="absolute top-1/3 left-1/2 w-[220px] h-[220px] rounded-full bg-orange/10 blur-3xl" />
        </div>

        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 py-14 sm:py-20 grid lg:grid-cols-[1.1fr_0.9fr] gap-10 lg:gap-14 items-start">
          <div>
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
              className="flex flex-wrap items-center gap-3 mb-5">
              <span className="inline-flex items-center gap-1.5 text-xs font-black uppercase tracking-wide bg-accent text-primary px-3 py-1.5 rounded-full shadow-lg shadow-accent/30">
                <IndianRupee size={13} strokeWidth={3} /> 50% Pay After Placement
              </span>
              <span className="uppercase text-[12px] tracking-[0.15em] font-bold text-accent flex items-center gap-2">
                <Sparkles size={14} /> Train. Build. Hire. — Chennai
              </span>
            </motion.div>
            <motion.h1 initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}
              className="text-white font-black text-4xl sm:text-5xl lg:text-6xl leading-[1.05] mb-5">
              Don't pay 50%<br />until you're <span className="text-accent">placed.</span>
            </motion.h1>
            <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
              className="text-[#cdc0dc] text-base sm:text-lg leading-relaxed max-w-xl mb-8">
              Vista Tech trains you in Generative AI and Full Stack Analytics — then puts an
              HR consultancy with 2,000+ placements behind your job search. Half your fee isn't due until
              you're holding an offer letter.
            </motion.p>
            <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}
              className="flex flex-wrap gap-3 mb-10">
              <a href="#lead-form" className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-accent text-primary font-bold text-sm shadow-lg shadow-accent/30">
                Book My Free Career Call <ArrowRight size={16} />
              </a>
              <a href="#tracks" className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full border border-white/25 text-white font-semibold text-sm hover:border-accent hover:text-accent transition-colors">
                See the 2 Tracks
              </a>
            </motion.div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-5 sm:gap-8 pt-6 border-t border-white/10">
              {[
                { n: 50, s: '+', l: 'Students Placed' },
                { n: 100, s: '+', l: 'Partner Placements' },
                { n: 50, s: '%', l: 'Fee Due After Placement' },
                { n: 0, s: '', l: 'Third-Party Job Apps' },
              ].map((s, i) => (
                <div key={i}>
                  <div className="text-2xl sm:text-3xl font-black text-accent"><Counter target={s.n} suffix={s.s} /></div>
                  <div className="text-[11px] uppercase tracking-wide text-[#9c8fb3] font-semibold mt-1">{s.l}</div>
                </div>
              ))}
            </div>
          </div>

          <motion.div id="lead-form" initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.2 }}
            className="bg-white/[0.06] border border-white/12 rounded-2xl p-6 sm:p-7 backdrop-blur-xl scroll-mt-20">
            <LeadForm idPrefix="hero" />
          </motion.div>
        </div>
      </section>

      {/* ── Success story videos ── */}
      <section className="py-14 sm:py-20 bg-[#0d0b14]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <Reveal className="text-center mb-8">
            <p className="uppercase text-[12px] tracking-widest font-bold text-accent mb-3">Hear it from them</p>
            <h2 className="text-white text-3xl sm:text-4xl font-black leading-tight">Watch two students tell their own story</h2>
          </Reveal>
          <Reveal className="grid sm:grid-cols-2 gap-5">
            {[
              { src: '/video/success-story-1.mp4', title: 'Success Story — from training to offer letter', source: 'Success Story 1 — Enroll Page' },
              { src: '/video/success-story-2.mp4', title: 'Success Story — a placed student shares their journey', source: 'Success Story 2 — Enroll Page' },
            ].map(v => (
              <div key={v.src} className="rounded-2xl overflow-hidden border border-white/12 shadow-2xl">
                <GatedVideo src={v.src} sourceLabel={v.source} playLabel="Watch the Success Story" />
                <p className="text-xs font-semibold text-primary bg-white px-4 py-3">{v.title}</p>
              </div>
            ))}
          </Reveal>
        </div>
      </section>

      {/* ── Process comparison: Normal vs Vista Tech HR Referral ── */}
      <section id="process" className="py-16 sm:py-24 bg-[#f1ece2]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <Reveal className="max-w-xl mb-12">
            <p className="uppercase text-[12px] tracking-widest font-bold text-accent-dark mb-3">Two very different paths</p>
            <h2 className="text-3xl sm:text-4xl font-black leading-tight">The normal placement process is broken. Ours skips it.</h2>
          </Reveal>

          <Reveal>
            <div className="grid lg:grid-cols-2 gap-6">
              {/* Normal process */}
              <div className="bg-white border border-primary/10 rounded-2xl p-7 sm:p-8">
                <p className="text-[11px] uppercase tracking-widest font-bold text-primary/40 mb-5">The normal placement process</p>
                <div className="flex flex-wrap gap-2 mb-6">
                  {NORMAL_STEPS.map((s, i) => (
                    <span key={s} className="flex items-center gap-2 text-xs font-semibold text-primary/60">
                      <span className="w-5 h-5 rounded-full bg-primary/10 text-primary/50 text-[10px] flex items-center justify-center font-bold">{i + 1}</span>
                      {s}
                    </span>
                  ))}
                </div>
                <div className="space-y-3">
                  <div className="flex items-start gap-3 bg-orange/10 rounded-xl p-4">
                    <span className="text-2xl font-black text-orange leading-none">7–9%</span>
                    <p className="text-xs text-primary/60 pt-1">of applicants ever receive an interview call, after aptitude / technical / GD / HR / final rounds.</p>
                  </div>
                  <div className="flex items-start gap-3 bg-primary/5 rounded-xl p-4">
                    <span className="text-2xl font-black text-primary/70 leading-none">~90%</span>
                    <p className="text-xs text-primary/60 pt-1">of people get rejected — after building the skill, portfolio and certificate anyway.</p>
                  </div>
                </div>
              </div>

              {/* Vista Tech process */}
              <div className="bg-primary rounded-2xl p-7 sm:p-8">
                <p className="text-[11px] uppercase tracking-widest font-bold text-accent mb-5">Vista Tech HR referral placement process</p>
                <div className="space-y-4">
                  {VISTA_STEPS.map((s, i) => (
                    <div key={s.title} className="flex items-start gap-3">
                      <span className="w-7 h-7 rounded-full bg-accent text-primary text-xs font-black flex items-center justify-center flex-none">{i + 1}</span>
                      <div>
                        <p className="text-white font-bold text-sm">{s.title}</p>
                        <p className="text-[#a89bbd] text-xs mt-0.5">{s.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ── Why HR consultancy matters ── */}
      <section className="py-16 sm:py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <Reveal className="max-w-xl mb-10">
            <p className="uppercase text-[12px] tracking-widest font-bold text-accent-dark mb-3">Importance of HR consultancy</p>
            <h2 className="text-3xl sm:text-4xl font-black leading-tight">Why going through a consultancy actually matters</h2>
          </Reveal>
          <Reveal>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {WHY_HR.map(w => (
                <div key={w.title} className="border border-primary/10 rounded-2xl p-6 bg-white">
                  <w.Icon size={22} className="text-accent-dark mb-3" />
                  <h4 className="font-bold text-sm mb-1.5">{w.title}</h4>
                  <p className="text-xs text-primary/55 leading-relaxed">{w.desc}</p>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* ── Program timeline ── */}
      <section id="program" className="py-16 sm:py-24 bg-[#f1ece2]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <Reveal className="max-w-xl mb-12">
            <p className="uppercase text-[12px] tracking-widest font-bold text-accent-dark mb-3">How the 100 days work</p>
            <h2 className="text-3xl sm:text-4xl font-black leading-tight mb-3">One structured path to your offer letter</h2>
            <p className="text-primary/60">The same program structure runs across every track — only the curriculum inside changes.</p>
          </Reveal>
          <Reveal>
            <div className="relative pl-7 sm:pl-8 max-w-2xl">
              <div className="absolute left-[9px] sm:left-[10px] top-1.5 bottom-1.5 w-0.5 bg-gradient-to-b from-accent to-orange" />
              {TIMELINE.map((item, i) => (
                <div key={i} className="relative pb-9 last:pb-0">
                  <div className="absolute -left-7 sm:-left-8 top-0.5 w-3.5 h-3.5 rounded-full bg-[#f1ece2] border-[3px] border-accent" />
                  <p className="text-[12px] font-bold tracking-wide text-orange mb-1">{item.day}</p>
                  <h4 className="text-lg font-bold mb-1">{item.title}</h4>
                  <p className="text-primary/60 text-sm max-w-md">{item.desc}</p>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* ── Tracks ── */}
      <section id="tracks" className="py-16 sm:py-24">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <Reveal className="max-w-xl mb-10">
            <p className="uppercase text-[12px] tracking-widest font-bold text-accent-dark mb-3">Choose your track</p>
            <h2 className="text-3xl sm:text-4xl font-black leading-tight mb-3">Two career tracks. One placement promise.</h2>
            <p className="text-primary/60">Every track shares the same pay-after-placement structure and HR consultancy support.</p>
          </Reveal>

          <div className="flex flex-wrap gap-2.5 mb-8">
            {TRACKS.map((t, i) => (
              <button
                key={t.id} onClick={() => setActiveTrack(i)}
                className={`px-4 sm:px-5 py-2.5 rounded-full text-xs sm:text-sm font-semibold border transition-colors ${
                  activeTrack === i ? 'bg-primary text-white border-primary' : 'bg-white text-primary/60 border-primary/15 hover:border-primary/40'
                }`}
              >
                {t.tab}
              </button>
            ))}
          </div>

          <AnimatePresence mode="wait">
            <motion.div
              key={track.id}
              initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.25 }}
              className="grid md:grid-cols-2 border border-primary/10 rounded-2xl overflow-hidden bg-white"
            >
              <div className="p-7 sm:p-9">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: `${track.color}18` }}>
                    <track.Icon size={20} style={{ color: track.color }} />
                  </div>
                  <span className="text-[11px] uppercase tracking-widest font-bold text-orange">{track.duration}</span>
                </div>
                <h3 className="text-2xl sm:text-3xl font-black mb-3">{track.title}</h3>
                <p className="text-primary/60 text-sm leading-relaxed mb-6">{track.desc}</p>
                <div className="flex flex-wrap gap-2">
                  {track.domains.map(d => (
                    <span key={d} className="text-xs font-medium px-3 py-1.5 rounded-full bg-[#f1ece2] text-primary/60 border border-primary/10">{d}</span>
                  ))}
                </div>
              </div>
              <div className="p-7 sm:p-9 bg-[#f8f5ee] border-t md:border-t-0 md:border-l border-primary/10">
                <p className="text-[11px] uppercase tracking-widest font-bold text-primary/40 mb-4">Placement roles</p>
                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                  {track.roles.map(r => (
                    <li key={r} className="text-sm text-primary/70 flex items-start gap-2">
                      <ArrowRight size={14} className="text-accent-dark mt-0.5 flex-none" />{r}
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
      </section>

      {/* ── Why Vista Tech ── */}
      <section className="py-16 sm:py-24 bg-[#f1ece2]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <Reveal className="max-w-xl mb-10">
            <p className="uppercase text-[12px] tracking-widest font-bold text-accent-dark mb-3">Why students choose Vista Tech</p>
            <h2 className="text-3xl sm:text-4xl font-black leading-tight">Built so the incentive is placement, not enrollment</h2>
          </Reveal>
          <Reveal>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-primary/10 border border-primary/10 rounded-2xl overflow-hidden">
              {WHY.map((w, i) => (
                <div key={i} className="bg-white p-5 sm:p-6">
                  <w.Icon size={20} className="text-accent-dark mb-3" />
                  <h4 className="text-sm font-bold mb-1.5 leading-snug">{w.title}</h4>
                  <p className="text-xs text-primary/55 leading-relaxed">{w.desc}</p>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* ── Mentors ── */}
      <section className="py-16 sm:py-24">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <Reveal className="max-w-xl mb-10">
            <p className="uppercase text-[12px] tracking-widest font-bold text-accent-dark mb-3">Who's teaching you</p>
            <h2 className="text-3xl sm:text-4xl font-black leading-tight mb-3">Mentors who build, teach and hire</h2>
            <p className="text-primary/60">Not assigned trainers — the people who designed the placement pipeline itself.</p>
          </Reveal>
          <Reveal>
            <div className="grid sm:grid-cols-3 gap-5">
              {MENTORS.map(m => (
                <div key={m.name} className="bg-white border border-primary/10 rounded-2xl p-6 sm:p-7">
                  <div className="w-12 h-12 rounded-full flex items-center justify-center text-white font-black text-sm mb-4" style={{ background: m.bg }}>
                    {m.initials}
                  </div>
                  <h4 className="font-bold text-lg mb-1">{m.name}</h4>
                  <p className="text-[11px] uppercase tracking-wide text-orange font-bold mb-3.5">{m.role}</p>
                  <blockquote className="text-sm text-primary/60 italic border-l-2 border-accent pl-3 leading-relaxed">{m.quote}</blockquote>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* ── Hiring partners ── */}
      <section className="py-14 sm:py-16 bg-[#f1ece2] border-y border-primary/10">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <Reveal className="text-center mb-8">
            <p className="uppercase text-[12px] tracking-widest font-bold text-accent-dark mb-2">Our consultants place candidates into</p>
            <h2 className="text-2xl sm:text-3xl font-black">Companies hiring through our HR consultancy partner</h2>
          </Reveal>
          <Reveal>
            <div className="flex flex-wrap justify-center gap-2.5">
              {HIRING_PARTNERS.map(p => (
                <span key={p} className="text-xs sm:text-sm font-bold px-4 py-2 rounded-full bg-white border border-primary/10 text-primary/70">
                  {p}
                </span>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* ── Success stories (real, named placements) ── */}
      <section id="success" className="py-16 sm:py-24">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <Reveal className="max-w-xl mb-10">
            <p className="uppercase text-[12px] tracking-widest font-bold text-accent-dark mb-3">Vista Tech success stories</p>
            <h2 className="text-3xl sm:text-4xl font-black leading-tight">Real students. Real companies. Real salaries.</h2>
          </Reveal>

          <Reveal>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
              {SUCCESS_STORIES.map(s => (
                <div key={s.name + s.company} className="bg-white border border-primary/10 rounded-xl overflow-hidden">
                  <div className="h-2 bg-gradient-to-r from-[#ffd6e7] to-[#ffc2dc]" />
                  <div className="p-4">
                    <div className="w-9 h-9 rounded-full bg-primary/8 text-primary font-black text-xs flex items-center justify-center mb-3">
                      {s.name.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase()}
                    </div>
                    <p className="font-bold text-sm leading-snug">{s.name}</p>
                    {s.edu && <p className="text-[11px] text-primary/40 font-medium mb-1.5">{s.edu}</p>}
                    <p className="text-xs text-primary/70 font-semibold leading-snug">{s.role}</p>
                    <p className="text-[11px] text-primary/50">at {s.company}</p>
                    <p className="text-xs font-black text-accent-dark mt-2">{s.salary}</p>
                  </div>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* ── Google Reviews ── */}
      <section className="py-16 sm:py-24 bg-[#f1ece2]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <Reveal className="max-w-xl mb-10">
            <p className="uppercase text-[12px] tracking-widest font-bold text-accent-dark mb-3">What students say on Google</p>
            <h2 className="text-3xl sm:text-4xl font-black leading-tight">Vista Tech Google Reviews</h2>
          </Reveal>
          <Reveal>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {REVIEWS.map(r => (
                <div key={r.name} className="bg-white border border-primary/10 rounded-2xl p-5">
                  <div className="flex items-center justify-between mb-2.5">
                    <p className="font-bold text-sm">{r.name}</p>
                    <span className="text-[11px] text-primary/40">{r.time}</span>
                  </div>
                  <Stars n={r.rating} />
                  <p className="text-xs text-primary/60 leading-relaxed mt-3">{r.text}</p>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* ── Placement partner ── */}
      <section className="py-16 sm:py-24">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <Reveal>
            <div className="bg-primary rounded-2xl p-7 sm:p-11 grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
              <div>
                <p className="uppercase text-[12px] tracking-widest font-bold text-accent mb-4">Our placement partner</p>
                <blockquote className="text-[#ebe6f2] text-base sm:text-lg leading-relaxed mb-5">
                  "With 20+ years in recruitment and 2,000+ successful placements, I believe true career success
                  comes from the right blend of skills, industry readiness and guidance. This program shifts the
                  focus from paying for courses to paying for results."
                </blockquote>
                <p className="text-accent text-sm font-semibold">— Rajeswari, Director, Infinite HR Consultants Pvt. Ltd.</p>
              </div>
              <div className="grid grid-cols-2 gap-5 sm:gap-6">
                {[
                  { n: 20, s: '+', l: 'Years in recruitment' },
                  { n: 2000, s: '+', l: 'Successful placements' },
                  { n: 150, s: '+', l: 'Vista Tech students placed' },
                  { n: 100, s: '+', l: 'Trained by Dhanush alone' },
                ].map((s, i) => (
                  <div key={i} className="border-l-2 border-accent pl-4">
                    <div className="text-2xl sm:text-3xl font-black text-accent"><Counter target={s.n} suffix={s.s} /></div>
                    <div className="text-xs text-[#b3a6c6] mt-1">{s.l}</div>
                  </div>
                ))}
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ── Pricing ── */}
      <section id="pricing" className="py-16 sm:py-24 bg-[#f1ece2]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <Reveal className="max-w-xl mb-10">
            <p className="uppercase text-[12px] tracking-widest font-bold text-accent-dark mb-3">Pricing &amp; the pay-after-placement offer</p>
            <h2 className="text-3xl sm:text-4xl font-black leading-tight mb-3">Half now. Half only once you're hired.</h2>
            <p className="text-primary/60">Same structure across both tracks. If you're not placed, the remaining 50% is never charged.</p>
          </Reveal>
          <Reveal>
            <div className="grid sm:grid-cols-2 gap-6">
              <div className="bg-white border border-primary/10 rounded-2xl p-7 sm:p-8">
                <h3 className="text-xl font-bold mb-1">Group Session (Online)</h3>
                <p className="text-primary/50 text-sm mb-6">Cohort-based live training</p>
                <div className="flex items-baseline gap-2 mb-1">
                  <span className="text-primary/50 text-lg font-semibold">₹</span>
                  <span className="text-4xl font-black">15,000</span>
                </div>
                <p className="text-primary/40 text-xs font-medium mb-6">to begin training today</p>
                <div className="flex justify-between text-sm py-2.5 border-t border-primary/10 text-primary/60"><span>Before placement</span><b className="text-primary">₹15,000</b></div>
                <div className="flex justify-between text-sm py-2.5 border-t border-primary/10 text-primary/60"><span>After placement</span><b className="text-primary">₹15,000</b></div>
                <div className="flex justify-between text-sm py-2.5 border-t border-primary/10 text-primary/60"><span>Total program fee</span><b className="text-primary">₹30,000</b></div>
                <a href="#lead-form" className="mt-6 block text-center py-3 rounded-full border border-primary/25 font-semibold text-sm hover:border-accent hover:text-accent-dark transition-colors">Start With Group Session</a>
              </div>
              <div className="bg-white border-2 border-accent rounded-2xl p-7 sm:p-8 relative shadow-xl shadow-accent/10">
                <span className="absolute -top-3 left-7 bg-accent text-primary text-[11px] font-bold uppercase tracking-wide px-3 py-1 rounded-full">In-person</span>
                <h3 className="text-xl font-bold mb-1">Group Session (Offline)</h3>
                <p className="text-primary/50 text-sm mb-6">Cohort-based in-person training</p>
                <div className="flex items-baseline gap-2 mb-1">
                  <span className="text-primary/50 text-lg font-semibold">₹</span>
                  <span className="text-4xl font-black">20,000</span>
                </div>
                <p className="text-primary/40 text-xs font-medium mb-6">to begin training today</p>
                <div className="flex justify-between text-sm py-2.5 border-t border-primary/10 text-primary/60"><span>Before placement</span><b className="text-primary">₹20,000</b></div>
                <div className="flex justify-between text-sm py-2.5 border-t border-primary/10 text-primary/60"><span>After placement</span><b className="text-primary">₹20,000</b></div>
                <div className="flex justify-between text-sm py-2.5 border-t border-primary/10 text-primary/60"><span>Total program fee</span><b className="text-primary">₹40,000</b></div>
                <a href="#lead-form" className="mt-6 block text-center py-3 rounded-full bg-accent text-primary font-bold text-sm shadow-lg shadow-accent/30">Start With Group Session</a>
              </div>
            </div>
            <div className="mt-6 flex flex-col sm:flex-row items-center justify-between gap-3 bg-white border border-accent-dark/30 rounded-2xl p-5 sm:p-6">
              <div>
                <p className="font-bold text-sm">Not ready for the full program?</p>
                <p className="text-xs text-primary/55">Register for ₹1000 and get a full week of live sessions before you decide.</p>
              </div>
              <a href={WHATSAPP_TRIAL_LINK} target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-accent-dark text-white font-bold text-xs whitespace-nowrap">
                Reserve ₹1000 Trial Week <ArrowRight size={14} />
              </a>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section id="faq" className="py-16 sm:py-24">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <Reveal className="mb-10">
            <p className="uppercase text-[12px] tracking-widest font-bold text-accent-dark mb-3">Frequently asked</p>
            <h2 className="text-3xl sm:text-4xl font-black leading-tight">Before you book your call</h2>
          </Reveal>
          <Reveal>
            <div className="border-t border-primary/10">
              {FAQS.map((f, i) => (
                <div key={i} className="border-b border-primary/10">
                  <button
                    onClick={() => setOpenFaq(openFaq === i ? null : i)}
                    className="w-full flex items-center justify-between gap-4 py-5 text-left"
                  >
                    <span className="font-bold text-base sm:text-lg">{f.q}</span>
                    <ChevronDown size={20} className={`flex-none text-accent transition-transform ${openFaq === i ? 'rotate-180' : ''}`} />
                  </button>
                  <AnimatePresence initial={false}>
                    {openFaq === i && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.22 }} className="overflow-hidden"
                      >
                        <p className="text-primary/60 text-sm sm:text-[15px] leading-relaxed pb-5 max-w-2xl">{f.a}</p>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* ── Final CTA ── */}
      <section className="py-16 sm:py-24 bg-gradient-to-br from-primary to-dark text-center">
        <div className="max-w-lg mx-auto px-4 sm:px-6">
          <Reveal>
            <p className="uppercase text-[12px] tracking-widest font-bold text-accent mb-4">Ready when you are</p>
            <h2 className="text-white text-3xl sm:text-4xl font-black leading-tight mb-4">Your Day 1 starts with a phone call.</h2>
            <p className="text-[#c2b6d4] mb-8">Free, no-obligation career counselling — a mentor helps you pick the right track before you commit a single rupee.</p>
          </Reveal>
          <Reveal>
            <div className="bg-white/[0.06] border border-white/12 rounded-2xl p-6 sm:p-7 text-left backdrop-blur-xl">
              <LeadForm idPrefix="footer" compact />
            </div>
          </Reveal>
        </div>
      </section>

      {/* ── Footer ── */}
      <footer className="bg-dark py-10">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="flex flex-col sm:flex-row justify-between gap-6 pb-6 border-b border-white/10">
            <div>
              <img src={logo} alt="Vista Tech" className="h-8 object-contain mb-3" />
              <p className="text-[#8f81a8] text-sm max-w-xs">Train. Build. Hire. A next-gen EdTech institute running Chennai's pay-after-placement Gen AI programs.</p>
            </div>
            <div className="flex flex-wrap gap-3 text-sm">
              <a href="tel:+919894449002" className="flex items-center gap-2 text-[#c2b6d4] hover:text-accent"><Phone size={14} />{PHONE_DISPLAY}</a>
              <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-[#c2b6d4] hover:text-accent"><Rocket size={14} />WhatsApp us</a>
              <Link to="/" className="flex items-center gap-2 text-[#c2b6d4] hover:text-accent">← Back to Home</Link>
            </div>
          </div>
          <p className="text-[#756890] text-xs mt-6">© Vista Tech · No.115, 7th Floor, Metro Towers, PH Road, Egmore, Chennai – 600084</p>
        </div>
      </footer>

      {/* ── Sticky mobile CTA ── */}
      <div className={`fixed left-0 right-0 bottom-0 z-50 sm:hidden px-4 py-3 bg-primary shadow-[0_-10px_30px_rgba(0,0,0,0.3)] transition-transform duration-300 ${showStickyCta ? 'translate-y-0' : 'translate-y-full'}`}>
        <a href="#lead-form" className="flex items-center justify-center gap-2 w-full py-3 rounded-full bg-accent text-primary font-bold text-sm">
          Book My Free Career Call <ArrowRight size={16} />
        </a>
      </div>
    </div>
    </ViewerProvider>
  );
};

export default Enrollment;
